import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import "./css/Gym.css";
import { TextField, Select, MenuItem, IconButton, Button, FormControl, InputLabel, Typography, Radio, RadioGroup, FormControlLabel, Checkbox, FormGroup } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { mostrarError, mostrarExito } from "../../utils/Alertas";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { useDispatch } from "react-redux";
import { faEye, faEyeSlash, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface SetType {
    peso: number | string;
    reps: string;
    tipo: "normal" | "dropset" | "descendente";
}

interface Exercise {
    id: string;
    nombre: string;
    sets: SetType[];
    nota: string;
    color?: "negro" | "azul" | "rojo" | "verde";
    dias?: string[];
    orderDias?: Record<string, number>;
    hidden?: boolean;
}

interface Props {
    exercise: Exercise;
    userId: string;
    onClose: () => void;
}

const ExerciseModalContent: React.FC<Props> = ({ exercise, userId, onClose }) => {
    const [data, setData] = useState(exercise);
    const dispatch = useDispatch();
    const [diasOpen, setDiasOpen] = useState(false);

    const DIAS_SEMANA = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

    const addSet = () => {
        setData({
            ...data,
            sets: [...data.sets, { peso: "", reps: "", tipo: "normal" }]
        });
    };

    const updateSet = (
        index: number,
        field: keyof SetType,
        value: string | number
    ) => {
        const newSets = [...data.sets];

        newSets[index] = {
            ...newSets[index],
            [field]: value
        };

        setData(prev => ({
            ...prev,
            sets: newSets
        }));
    };

    const removeSet = (index: number) => {
        const newSets = data.sets.filter((_: any, i: number) => i !== index);
        setData({ ...data, sets: newSets });
    };

    const toggleDia = (dia: string) => {
        const current = data.dias ?? [];
        const updated = current.includes(dia)
            ? current.filter(d => d !== dia)
            : [...current, dia];
        setData({ ...data, dias: updated });
    };

    const toggleHidden = () => {
        setData(prev => ({ ...prev, hidden: !prev.hidden }));
    };

    const save = async () => {
        dispatch(showLoading());
        try {
            await updateDoc(doc(db, "exercises", data.id), {
                nombre: data.nombre,
                sets: data.sets,
                nota: data.nota,
                color: data.color ?? "negro",
                dias: data.dias ?? [],
                hidden: data.hidden ?? false
            });

            onClose();
            mostrarExito('Ejercicio editado correctamente.')
        } catch (error) {
            console.error("Error editado ejercicio: ", error);
            mostrarError('Error editado ejercicio.');
        } finally {
            dispatch(hideLoading());
        }
    };

    return (
        <div className="resultado-content">
            <div className="resultado-header">
                <h2>Editar ejercicio</h2>
            </div>

            <div className="resultado-body">
                {/* Nombre */}
                <TextField
                    label="Nombre del ejercicio"
                    value={data.nombre}
                    onChange={(e) => setData({ ...data, nombre: e.target.value })}
                    fullWidth
                    margin="normal"
                    style={{ marginBottom: 0, marginTop: 5 }}
                />

                <FormControl fullWidth>
                    <RadioGroup
                        row
                        value={data.color ?? "negro"}
                        onChange={(e) =>
                            setData({
                                ...data,
                                color: e.target.value as "negro" | "azul" | "rojo" | "verde"
                            })
                        }
                        sx={{
                            flexWrap: "wrap",
                            justifyContent: "flex-start",
                            width: "100%",
                            gap: 0.5
                        }}
                    >
                        <FormControlLabel
                            value="negro"
                            control={
                                <Radio
                                    size="small"
                                    sx={{
                                        p: 0.5,
                                        color: "var(--exercise-color-negro)",
                                        "&.Mui-checked": {
                                            color: "var(--exercise-color-negro)"
                                        }
                                    }}
                                />
                            }
                            label="Negro"
                            sx={{
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    color: "var(--exercise-color-negro)",
                                    fontWeight: 600,
                                    fontSize: "0.95rem"
                                }
                            }}
                        />

                        <FormControlLabel
                            value="azul"
                            control={
                                <Radio
                                    size="small"
                                    sx={{
                                        p: 0.5,
                                        color: "#004aad",
                                        "&.Mui-checked": {
                                            color: "#004aad"
                                        }
                                    }}
                                />
                            }
                            label="Azul"
                            sx={{
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    color: "#004aad",
                                    fontWeight: 600,
                                    fontSize: "0.95rem"
                                }
                            }}
                        />

                        <FormControlLabel
                            value="rojo"
                            control={
                                <Radio
                                    size="small"
                                    sx={{
                                        p: 0.5,
                                        color: "#b40808",
                                        "&.Mui-checked": {
                                            color: "#b40808"
                                        }
                                    }}
                                />
                            }
                            label="Rojo"
                            sx={{
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    color: "#b40808",
                                    fontWeight: 600,
                                    fontSize: "0.95rem"
                                }
                            }}
                        />

                        <FormControlLabel
                            value="verde"
                            control={
                                <Radio
                                    size="small"
                                    sx={{
                                        p: 0.5,
                                        color: "#1a7a1a",
                                        "&.Mui-checked": {
                                            color: "#1a7a1a"
                                        }
                                    }}
                                />
                            }
                            label="Verde"
                            sx={{
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    color: "#1a7a1a",
                                    fontWeight: 600,
                                    fontSize: "0.95rem"
                                }
                            }}
                        />
                    </RadioGroup>
                </FormControl>

                {/* Sets */}
                <Typography variant="subtitle1">
                    Series
                </Typography>

                {data.sets.map((set: any, i: number) => (
                    <div className="set-row" key={i}>
                        <TextField
                            label="Peso"
                            type="number"
                            value={set.peso}
                            onChange={(e) => updateSet(i, "peso", e.target.value)}
                            size="small"
                            className="peso"
                        />

                        <TextField
                            label="Reps"
                            value={set.reps}
                            onChange={(e) => updateSet(i, "reps", e.target.value)}
                            size="small"
                            className="reps"
                        />

                        <FormControl size="small" className="tipo">
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                value={set.tipo}
                                label="Tipo"
                                onChange={(e) => updateSet(i, "tipo", e.target.value)}
                            >
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="dropset">Dropset</MenuItem>
                                <MenuItem value="descendente">Descendente</MenuItem>
                            </Select>
                        </FormControl>

                        <IconButton
                            onClick={() => removeSet(i)}
                            color="error"
                            className="delete"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </div>
                ))}

                <Button
                    variant="outlined"
                    onClick={addSet}
                >
                    + Añadir serie
                </Button>

                {/* Días de la semana — desplegable */}
                <div className="dias-section">
                    <button
                        type="button"
                        className="dias-toggle"
                        onClick={() => setDiasOpen(p => !p)}
                    >
                        <span>
                            {(data.dias ?? []).length > 0
                                ? data.dias!.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(" | ")
                                : "Días"
                            }
                        </span>
                        <FontAwesomeIcon icon={diasOpen ? faChevronUp : faChevronDown} />
                    </button>

                    {diasOpen && (
                        <FormGroup sx={{ pt: 0.5 }}>
                            {DIAS_SEMANA.map(dia => (
                                <FormControlLabel
                                    key={dia}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={(data.dias ?? []).includes(dia)}
                                            onChange={() => toggleDia(dia)}
                                            sx={{
                                                p: 0.5,
                                                color: "#5a67d8",
                                                "&.Mui-checked": { color: "#5a67d8" }
                                            }}
                                        />
                                    }
                                    label={dia.charAt(0).toUpperCase() + dia.slice(1)}
                                    sx={{
                                        margin: 0,
                                        "& .MuiFormControlLabel-label": { fontSize: "0.9rem" }
                                    }}
                                />
                            ))}
                        </FormGroup>
                    )}
                </div>

                {/* Ocultar ejercicio */}
                <button
                    type="button"
                    className="toggle-hidden-btn"
                    onClick={toggleHidden}
                >
                    <FontAwesomeIcon icon={data.hidden ? faEyeSlash : faEye} />
                    <span>{data.hidden ? "Ejercicio oculto" : "Ejercicio visible"}</span>
                </button>

                {/* Nota */}
                <TextField
                    label="Notas"
                    multiline
                    rows={3}
                    value={data.nota}
                    onChange={(e) => setData({ ...data, nota: e.target.value })}
                    fullWidth
                    margin="normal"
                    style={{ marginTop: 0, marginBottom: 0 }}
                />
            </div>

            <div className="resultado-footer">
                <button
                    className="main-button"
                    onClick={save}
                >
                    Guardar
                </button>

                <button
                    className="delete-primary-button"
                    onClick={onClose}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );
};

export default ExerciseModalContent;
